import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Users, Star, Check, TestTube2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [customAmount, setCustomAmount] = useState(1);

  useEffect(() => {
    fetchPropertyDetails();
    fetchReviews();
  }, [id]);

  const fetchPropertyDetails = async () => {
    try {
      const response = await api.getProperty(id);
      setProperty(response.data);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      toast.error('Property not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await api.getReviews(id);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to book');
      return;
    }

    const formData = new FormData(e.target);
    try {
      const bookingData = {
        property_id: id,
        event_type: formData.get('event_type'),
        start_date: startDate ? startDate.toISOString() : new Date(formData.get('start_date')).toISOString(),
        end_date: endDate ? endDate.toISOString() : new Date(formData.get('end_date')).toISOString(),
        guests: parseInt(formData.get('guests')),
        special_requests: formData.get('special_requests'),
        booking_type: formData.get('booking_type'),
      };

      const response = await api.createBooking(bookingData);
      toast.success('Booking created successfully!');
      setShowBookingDialog(false);

      // Use custom amount if test mode is enabled, otherwise use actual booking amount
      let amount = bookingData.booking_type === 'token' 
        ? response.data.token_amount 
        : response.data.total_amount;
      
      if (testMode && customAmount > 0) {
        amount = customAmount;
        toast.info(`Test Mode: Using custom amount ₹${customAmount}`);
      }

      // Create Razorpay order
      const orderResponse = await api.createRazorpayOrder({
        booking_id: response.data.id,
        amount: amount,
        payment_type: bookingData.booking_type,
      });

      // Initialize Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: 'My Corporate Hub',
        description: `Booking for ${property.name}`,
        image: 'https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png',
        order_id: orderResponse.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            // Verify payment
            await api.verifyRazorpayPayment({
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              booking_id: response.data.id,
            });
            toast.success('Payment successful!');
            navigate('/payment-success?session_id=' + razorpayResponse.razorpay_order_id);
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
          contact: user.phone || '',
        },
        theme: {
          color: '#1e3a5f',
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-800">Loading...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-800">Property not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/properties')}
          className="flex items-center gap-2 text-navy hover:text-gold mb-6 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Properties</span>
        </button>

        {/* Property Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          <img
            src={property.images[0]}
            alt={property.name}
            className="w-full h-[400px] object-cover rounded-sm"
            data-testid="property-main-image"
          />
          <div className="grid grid-cols-2 gap-4">
            {property.images.slice(1, 5).map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`${property.name} ${idx + 2}`}
                className="w-full h-[192px] object-cover rounded-sm"
              />
            ))}
          </div>
        </div>

        {/* Property Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-8">
            <div>
              <div className="flex items-start justify-between mb-4">
                <h1 className="luxury-text text-4xl font-medium text-midnight-navy" data-testid="property-detail-name">
                  {property.name}
                </h1>
                {property.rating > 0 && (
                  <div className="flex items-center gap-2 bg-champagne-gold/10 px-4 py-2 rounded-sm">
                    <Star className="w-5 h-5 fill-champagne-gold text-champagne-gold" />
                    <span className="font-semibold">{property.rating}</span>
                    <span className="text-sm text-slate-800">({property.total_reviews} reviews)</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 text-slate-800">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{property.location}, {property.city}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>Up to {property.capacity} guests</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="luxury-text text-2xl font-medium mb-4">About this venue</h2>
              <p className="text-slate-800 leading-relaxed mb-4">{property.description}</p>
              
              {/* Property Quick Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 p-4 bg-slate-50 rounded-lg">
                {property.property_type && (
                  <div className="text-center">
                    <div className="text-sm text-slate-500">Property Type</div>
                    <div className="font-semibold text-navy capitalize">{property.property_type}</div>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-sm text-slate-500">Max Capacity</div>
                  <div className="font-semibold text-navy">{property.capacity} guests</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-slate-500">Price per Day</div>
                  <div className="font-semibold text-navy">₹{property.price_per_day?.toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-slate-500">Location</div>
                  <div className="font-semibold text-navy">{property.city}</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="luxury-text text-2xl font-medium mb-4">Amenities</h2>
              <div className="grid grid-cols-2 gap-3">
                {property.amenities.map((amenity, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-silk" />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviews */}
            {reviews.length > 0 && (
              <div>
                <h2 className="luxury-text text-2xl font-medium mb-6">Guest Reviews</h2>
                <div className="space-y-4">
                  {reviews.slice(0, 5).map((review) => (
                    <div key={review.id} className="bg-slate-50 p-6 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{review.client_name}</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-champagne-gold text-champagne-gold" />
                          <span className="text-sm">{review.rating}</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-800">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Card */}
          <div className="md:col-span-1">
            <div className="sticky top-24 bg-white border border-slate-200 p-8 rounded-sm shadow-sm">
              <div className="mb-6">
                <span className="luxury-text text-4xl font-semibold text-midnight-navy" data-testid="property-detail-price">
                  ₹{property.price_per_day.toLocaleString()}
                </span>
                <span className="text-slate-800 ml-2">per day</span>
              </div>
              <Button
                onClick={() => user ? setShowBookingDialog(true) : toast.error('Please login to book')}
                className="w-full rounded-sm bg-midnight-navy hover:bg-midnight-navy/90 text-white py-6"
                data-testid="book-now-button"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="luxury-text text-2xl">Complete Your Booking</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBooking} className="space-y-4">
            <div>
              <Label htmlFor="event_type">Event Type</Label>
              <Input id="event_type" name="event_type" required placeholder="Conference, Wedding, etc." data-testid="booking-event-type-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Check-In</Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Check-in date"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                  data-testid="booking-start-date-input"
                />
                <input type="hidden" name="start_date" value={startDate ? startDate.toISOString().split('T')[0] : ''} />
              </div>
              <div>
                <Label htmlFor="end_date">Check-Out</Label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate || new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Check-out date"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                  data-testid="booking-end-date-input"
                />
                <input type="hidden" name="end_date" value={endDate ? endDate.toISOString().split('T')[0] : ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="guests">Number of Guests</Label>
              <Input id="guests" name="guests" type="number" required data-testid="booking-guests-input" />
            </div>
            <div>
              <Label htmlFor="booking_type">Booking Type</Label>
              <Select name="booking_type" defaultValue="full" required>
                <SelectTrigger data-testid="booking-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="token">Pay 20% Token</SelectItem>
                  <SelectItem value="full">Pay Full Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Test Mode for Payment Testing */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="testMode" 
                  checked={testMode}
                  onCheckedChange={(checked) => setTestMode(checked)}
                />
                <Label htmlFor="testMode" className="flex items-center gap-2 cursor-pointer">
                  <TestTube2 className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-medium">Test Mode (Custom Amount)</span>
                </Label>
              </div>
              {testMode && (
                <div className="space-y-2">
                  <Label htmlFor="customAmount" className="text-amber-700 text-sm">
                    Enter custom payment amount (₹)
                  </Label>
                  <Input 
                    id="customAmount"
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 1)}
                    className="bg-white border-amber-300 focus:border-amber-500"
                    placeholder="Enter amount in rupees"
                  />
                  <p className="text-xs text-amber-600">
                    ⚠️ Test payment will be processed for ₹{customAmount} instead of actual booking cost
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="special_requests">Special Requests (Optional)</Label>
              <Textarea id="special_requests" name="special_requests" data-testid="booking-requests-textarea" />
            </div>
            
            {/* Razorpay Verified Badge */}
            <div className="flex flex-col items-center gap-3 py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-slate-900 font-semibold">Secure Payments Powered By</span>
              <div className="flex items-center gap-4">
                <img 
                  src="https://cdn.razorpay.com/logo.svg" 
                  alt="Razorpay" 
                  className="h-5"
                />
                <div className="w-px h-6 bg-gray-300"></div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-4"
                />
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-6"
                />
                <div className="w-px h-6 bg-gray-300"></div>
                <img 
                  src="https://badges.razorpay.com/badge-dark.png" 
                  alt="Razorpay Trusted Business" 
                  className="h-7"
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full rounded-sm bg-midnight-navy hover:bg-midnight-navy/90" data-testid="booking-confirm-button">
              Proceed to Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
